import React, { useState } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Loader2, MessageSquare, Send, UserRound, Wallet } from 'lucide-react';
import { useLanguage, interpolate } from '../../context/LanguageContext';
import { useHideRecaptchaBadge } from '../../hooks/useHideRecaptchaBadge';
import ScrollReveal from '../ScrollReveal/ScrollReveal';
import ContactDotBackground from './ContactDotBackground';
import './Contact.css';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
type BudgetMode = 'range' | 'lets-talk';
type BudgetCurrency = 'USD' | 'CAD' | 'PEN';

const FORMSPREE_FORM_ID = process.env.REACT_APP_FORMSPREE_FORM_ID;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const formatAmount = (value: string) => {
  const num = Number(value);
  if (Number.isNaN(num)) {
    return value;
  }
  return num.toLocaleString();
};

const buildBudgetLabel = (
  mode: BudgetMode,
  currency: BudgetCurrency,
  min: string,
  max: string,
  letsTalkLabel: string,
  upToLabel: string
) => {
  if (mode === 'lets-talk') {
    return letsTalkLabel;
  }

  const minValue = min.trim();
  const maxValue = max.trim();

  if (!minValue && !maxValue) {
    return '-';
  }

  if (minValue && maxValue) {
    return `${currency} ${formatAmount(minValue)} – ${formatAmount(maxValue)}`;
  }

  if (minValue) {
    return `${currency} ${formatAmount(minValue)}+`;
  }

  return `${currency} ${upToLabel} ${formatAmount(maxValue)}`;
};

const ContactForm: React.FC = () => {
  const { t } = useLanguage();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [budgetMode, setBudgetMode] = useState<BudgetMode>('range');
  const [budgetCurrency, setBudgetCurrency] = useState<BudgetCurrency>('USD');
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [budgetError, setBudgetError] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');

  const resetStatus = () => {
    if (status !== 'idle') {
      setStatus('idle');
    }
    if (budgetError) {
      setBudgetError('');
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    resetStatus();
  };

  const handleBudgetMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBudgetMin(e.target.value);
    setBudgetMode('range');
    resetStatus();
  };

  const handleBudgetMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBudgetMax(e.target.value);
    setBudgetMode('range');
    resetStatus();
  };

  const handleCurrencySelect = (currency: BudgetCurrency) => {
    setBudgetCurrency(currency);
    setBudgetMode('range');
    resetStatus();
  };

  const handleLetsTalkToggle = () => {
    if (budgetMode === 'lets-talk') {
      setBudgetMode('range');
    } else {
      setBudgetMode('lets-talk');
      setBudgetMin('');
      setBudgetMax('');
    }
    resetStatus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!FORMSPREE_FORM_ID) {
      setStatus('error');
      return;
    }

    if (
      budgetMode === 'range' &&
      budgetMin.trim() &&
      budgetMax.trim() &&
      Number(budgetMin) > Number(budgetMax)
    ) {
      setBudgetError(t.contact.budgetRangeError);
      return;
    }

    const subject = interpolate(t.contact.mailSubject, { name: formData.name });
    const budgetLabel = buildBudgetLabel(
      budgetMode,
      budgetCurrency,
      budgetMin,
      budgetMax,
      t.contact.budgetLetsTalk,
      t.contact.budgetUpTo
    );

    setStatus('submitting');

    try {
      let captchaToken: string | undefined;
      if (RECAPTCHA_SITE_KEY && executeRecaptcha) {
        captchaToken = await executeRecaptcha('contact_submit');
      }

      const payload: Record<string, string> = {
        name: formData.name,
        email: formData.email,
        company: formData.company || '-',
        budget: budgetLabel,
        message: formData.message,
        _subject: subject,
      };

      if (captchaToken) {
        payload['g-recaptcha-response'] = captchaToken;
      }

      const response = await fetch(`https://formspree.io/f/${FORMSPREE_FORM_ID}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Formspree request failed');
      }

      setStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        message: '',
      });
      setBudgetMode('range');
      setBudgetCurrency('USD');
      setBudgetMin('');
      setBudgetMax('');
      setBudgetError('');
    } catch {
      setStatus('error');
    }
  };

  const isRangeMode = budgetMode === 'range';
  const isSubmitting = status === 'submitting';

  return (
    <section className="contact editorial-section section-shell section-after-curve" id="contact">
      <ContactDotBackground />

      <div className="editorial-container contact-content">
        <ScrollReveal>
          <header className="contact-header">
            <h2 className="section-heading">
              {t.contact.titleLine1}{' '}
              <span className="section-heading-accent">{t.contact.titleLine2}</span>
            </h2>
            <p className="section-intro">{t.contact.subtitle}</p>
            <p className="contact-bilingual-note">{t.contact.bilingualNote}</p>
          </header>
        </ScrollReveal>

        <ScrollReveal delay={100}>
          <div className="contact-form-shell">
            <div className="contact-form-card">
              <form className="contact-form" onSubmit={handleSubmit}>
                <section className="contact-form-section">
                  <div className="contact-form-section-head">
                    <h3>{t.contact.formSectionYou}</h3>
                    <UserRound size={18} aria-hidden="true" />
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        placeholder=" "
                      />
                      <label htmlFor="name">{t.contact.name}</label>
                      <span className="form-field-glow" aria-hidden="true" />
                    </div>
                    <div className="form-field">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        placeholder=" "
                      />
                      <label htmlFor="email">{t.contact.email}</label>
                      <span className="form-field-glow" aria-hidden="true" />
                    </div>
                  </div>

                  <div className="form-field">
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      placeholder=" "
                    />
                    <label htmlFor="company">{t.contact.company}</label>
                    <span className="form-field-glow" aria-hidden="true" />
                  </div>
                </section>

                <section className="contact-form-section">
                  <fieldset className="budget-fieldset" disabled={isSubmitting}>
                    <div className="contact-form-section-head">
                      <h3>{t.contact.formSectionBudget}</h3>
                      <Wallet size={18} aria-hidden="true" />
                    </div>

                    <div className={`budget-panel ${isRangeMode ? '' : 'budget-panel--lets-talk'}`}>
                      <div className="budget-currency-block">
                        <span className="budget-sub-label">{t.contact.budgetCurrencyLabel}</span>
                        <div className="budget-currency-options" role="group" aria-label={t.contact.budgetCurrencyLabel}>
                          {t.contact.budgetCurrencies.map((currency) => (
                            <button
                              key={currency.value}
                              type="button"
                              className={`budget-option ${
                                isRangeMode && budgetCurrency === currency.value ? 'active' : ''
                              }`}
                              onClick={() => handleCurrencySelect(currency.value as BudgetCurrency)}
                              disabled={isSubmitting}
                              aria-pressed={isRangeMode && budgetCurrency === currency.value}
                            >
                              {currency.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="budget-range-row">
                        <div className="budget-range-field">
                          <label htmlFor="budgetMin">{t.contact.budgetMinLabel}</label>
                          <div className="budget-input-wrap">
                            <span className="budget-input-prefix" aria-hidden="true">
                              {isRangeMode ? budgetCurrency : '—'}
                            </span>
                            <input
                              type="number"
                              id="budgetMin"
                              name="budgetMin"
                              min="0"
                              step="1"
                              value={budgetMin}
                              onChange={handleBudgetMinChange}
                              disabled={isSubmitting}
                            />
                          </div>
                        </div>

                        <div className="budget-range-field">
                          <label htmlFor="budgetMax">{t.contact.budgetMaxLabel}</label>
                          <div className="budget-input-wrap">
                            <span className="budget-input-prefix" aria-hidden="true">
                              {isRangeMode ? budgetCurrency : '—'}
                            </span>
                            <input
                              type="number"
                              id="budgetMax"
                              name="budgetMax"
                              min="0"
                              step="1"
                              value={budgetMax}
                              onChange={handleBudgetMaxChange}
                              disabled={isSubmitting}
                            />
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        className={`budget-option budget-lets-talk ${budgetMode === 'lets-talk' ? 'active' : ''}`}
                        onClick={handleLetsTalkToggle}
                        disabled={isSubmitting}
                        aria-pressed={budgetMode === 'lets-talk'}
                      >
                        {t.contact.budgetLetsTalk}
                      </button>
                    </div>

                    {budgetError && (
                      <p className="budget-range-error" role="alert">
                        {budgetError}
                      </p>
                    )}
                  </fieldset>
                </section>

                <section className="contact-form-section">
                  <div className="contact-form-section-head">
                    <h3>{t.contact.formSectionProject}</h3>
                    <MessageSquare size={18} aria-hidden="true" />
                  </div>

                  <div className="form-field form-field--textarea">
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      rows={5}
                      placeholder=" "
                    />
                    <label htmlFor="message">{t.contact.message}</label>
                    <span className="form-field-glow" aria-hidden="true" />
                  </div>
                </section>

                {status === 'success' && (
                  <p className="contact-form-status contact-form-status--success" role="status">
                    {t.contact.submitSuccess}
                  </p>
                )}
                {status === 'error' && (
                  <p className="contact-form-status contact-form-status--error" role="alert">
                    {t.contact.submitError}
                  </p>
                )}

                <button
                  type="submit"
                  className="contact-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <Loader2 size={18} className="contact-submit-spinner" />
                  ) : (
                    <Send size={18} />
                  )}
                  <span>{isSubmitting ? t.contact.submitting : t.contact.submit}</span>
                </button>
              </form>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

const Contact: React.FC = () => {
  useHideRecaptchaBadge(Boolean(RECAPTCHA_SITE_KEY));

  if (RECAPTCHA_SITE_KEY) {
    return (
      <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
        <ContactForm />
      </GoogleReCaptchaProvider>
    );
  }

  return <ContactForm />;
};

export default Contact;
